import { RULESET_VERSION } from '@rhcst/shared';
import type { RuleResult, RiskAssessment, ComponentScore, ComponentScores } from '@rhcst/shared';
import { relativeAge, absoluteTime } from '../lib/format.js';

/**
 * The full risk breakdown.
 *
 * This is the panel that has to earn the operator's trust, so it shows the
 * reasoning rather than the conclusion. A score with no visible derivation is
 * indistinguishable from a guess.
 *
 * The redesign adds one thing and moves nothing: a **"Why N, in short"** panel
 * above the table, carrying the two or three biggest penalties in plain language
 * with their raw figures underneath. The full table remains below it, unchanged
 * in content. The problem it solves is that the table is complete, honest, and
 * takes twenty seconds to read — while the question the operator is actually
 * asking ("why is this 50?") deserves an answer in about two.
 *
 * INVARIANT 4: the AI summary stays below the rules, visually secondary, and
 * labelled with its model. ADR-005 — rules decide, AI explains. Promoting the
 * readable paragraph above the table is tempting precisely because it reads
 * better, and that is the trap: the operator would be trusting a language
 * model's summary of a decision instead of the decision.
 *
 * v2 adds a **component-score breakdown** between "why in short" and the
 * full rule table: the six weighted `RiskAssessment.components` fields
 * (Phase 11, Slice 11G) computed and persisted by the backend but never
 * rendered anywhere until now. It is still rule/weight-derived data, not the
 * AI summary — it stays above the AI summary block, same rule as the table.
 * `finalScore` is deliberately not shown here: it is a separate number from
 * `score`/`verdict` above by design (not yet wired into any trading gate),
 * and this panel does not invent a second "the score" to avoid confusing
 * the one that actually drives policy.
 *
 * INVARIANT 7 (v2): never fabricate a value the backend doesn't compute.
 * `executionSafety` always has `weight: 0` — no buy/sell simulator exists
 * yet — and renders as literal "Not implemented" text, never a numeral or a
 * bar standing in for one.
 */

export interface RiskSignalView {
  id: string;
  score: number;
  verdict: string;
  has_veto: boolean;
  complete: boolean;
  ruleset_version: string;
  stale_ruleset: boolean;
  ai_summary: string | null;
  ai_model: string | null;
  ai_generated_at: string | null;
  trigger: string;
  created_at: string;
  /** Full assessment for the latest signal; null for historical entries. */
  rule_results: unknown;
}

const OUTCOME_LABEL: Record<string, string> = {
  pass: 'pass',
  fail: 'fail',
  unknown: 'no data',
  // Never "0". A skipped rule did not run; it did not run and find nothing
  // wrong, and those are different claims.
  'n/a': 'n/a',
};

/** Narrow the persisted jsonb into the shape the panel renders. */
function readAssessment(value: unknown): RiskAssessment | null {
  if (value === null || typeof value !== 'object') return null;
  const candidate = value as Partial<RiskAssessment>;
  if (!Array.isArray(candidate.rules)) return null;
  if (typeof candidate.score !== 'number') return null;
  return candidate as RiskAssessment;
}

function RuleRow({ rule }: { rule: RuleResult }) {
  return (
    <li className="rule" data-outcome={rule.outcome} data-veto={rule.veto}>
      {/* A bar sized to the penalty, so magnitude is visible without reading
          the number. */}
      <span
        className="rule-bar"
        style={rule.penalty > 0 ? { minHeight: `${Math.min(64, 20 + rule.penalty)}px` } : undefined}
        aria-hidden="true"
      />

      <span>
        <span className="row" style={{ gap: '0.5rem', flexWrap: 'wrap' }}>
          <span className="rule-label">{rule.label}</span>
          {rule.veto ? (
            <span className="veto-block">Veto</span>
          ) : (
            <span className="chip" data-tone={rule.outcome === 'fail' ? 'alarm' : 'muted'}>
              {OUTCOME_LABEL[rule.outcome] ?? rule.outcome}
            </span>
          )}
        </span>
        <span className="rule-detail">{rule.detail}</span>
        {(rule.observed !== undefined || rule.threshold !== undefined) && (
          <span className="rule-values">
            {rule.observed !== undefined && rule.observed !== null && (
              <>observed {String(rule.observed)}</>
            )}
            {rule.threshold !== undefined && rule.threshold !== null && (
              <> · threshold {String(rule.threshold)}</>
            )}
          </span>
        )}
      </span>

      {rule.penalty > 0 && <span className="rule-penalty">−{rule.penalty}</span>}
    </li>
  );
}

/** The two or three factors that actually drove the number. */
function WhyInShort({ rules, score }: { rules: RuleResult[]; score: number }) {
  const drivers = rules
    .filter((rule) => rule.penalty > 0 || rule.veto)
    .sort((a, b) => {
      // A veto outranks any penalty: it is not a large deduction, it is a
      // different kind of statement.
      if (a.veto !== b.veto) return a.veto ? -1 : 1;
      return b.penalty - a.penalty;
    })
    .slice(0, 3);

  if (drivers.length === 0) return null;

  return (
    <div className="stack-sm">
      <span className="label">Why {score}, in short</span>
      <div className="why-panel">
        {drivers.map((rule) => (
          <div className="why-card" key={rule.id} data-outcome={rule.outcome}>
            <span className="why-label">{rule.label}</span>
            {(rule.observed !== undefined || rule.threshold !== undefined) && (
              <span className="why-figures">
                {rule.observed !== undefined && rule.observed !== null && (
                  <>{String(rule.observed)}</>
                )}
                {rule.threshold !== undefined && rule.threshold !== null && (
                  <> vs {String(rule.threshold)}</>
                )}
              </span>
            )}
            <span className="why-cost">{rule.veto ? 'veto' : `−${rule.penalty} points`}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

const COMPONENT_ORDER = [
  'baseViability',
  'liquidity',
  'contractSafety',
  'actorReputation',
  'provenance',
  'marketContext',
] as const;

const COMPONENT_LABEL: Record<(typeof COMPONENT_ORDER)[number], string> = {
  baseViability: 'Base viability',
  liquidity: 'Liquidity',
  contractSafety: 'Contract safety',
  actorReputation: 'Actor reputation',
  provenance: 'Provenance',
  marketContext: 'Market context',
};

type Tier = 'good' | 'watch' | 'risky' | 'avoid';

/** Same bands the flat verdict already uses, so a component's colour never
 *  disagrees with what "qualified"/"watch"/"risky"/"avoid" mean for this
 *  pair's own ruleset version. */
function tierFor(score: number, bands: RiskAssessment['verdictBands']): Tier {
  if (score < bands.avoidBelow) return 'avoid';
  if (score < bands.riskyBelow) return 'risky';
  if (score < bands.qualifiedAtOrAbove) return 'watch';
  return 'good';
}

function ComponentRow({
  componentKey,
  component,
  bands,
}: {
  componentKey: (typeof COMPONENT_ORDER)[number];
  component: ComponentScore;
  bands: RiskAssessment['verdictBands'];
}) {
  const tier = tierFor(component.score, bands);
  return (
    <div className="component-row">
      <div className="component-head">
        <span>
          <span className="component-label">{COMPONENT_LABEL[componentKey]}</span>
          <span className="component-weight">{Math.round(component.weight * 100)}% weight</span>
        </span>
        <span className="component-score" data-tier={tier}>
          {component.score}
          <span className="component-score-of"> / 100</span>
        </span>
      </div>
      <div className="component-bar-track">
        <div
          className="component-bar-fill"
          data-tier={tier}
          style={{ width: `${component.score}%` }}
        />
      </div>
      <p className="component-detail">{component.detail}</p>
    </div>
  );
}

/** The Slice 11G component-score breakdown. Additive — never read anywhere
 *  that the flat `score`/`verdict` come from. Absent on any assessment
 *  computed before Slice 11G shipped, so the section is simply omitted for
 *  older signals rather than showing a broken or fabricated version of it. */
function ComponentBreakdown({
  components,
  bands,
}: {
  components: ComponentScores;
  bands: RiskAssessment['verdictBands'];
}) {
  return (
    <div className="stack-sm">
      <span className="label">Component breakdown</span>
      <div className="inset-card">
        <div className="components">
          {COMPONENT_ORDER.map((key) => (
            <ComponentRow key={key} componentKey={key} component={components[key]} bands={bands} />
          ))}
        </div>

        <div className="component-unweighted">
          <div className="component-unweighted-card">
            <span className="label label-dim">Confidence</span>
            <p className="data" style={{ marginTop: '0.375rem' }}>
              {components.confidence}%
            </p>
            <p className="panel-note">Unweighted — data completeness, not a score input.</p>
          </div>
          <div className="component-unweighted-card">
            <span className="label label-dim">Execution safety</span>
            <p style={{ marginTop: '0.375rem' }}>
              <span className="component-unweighted-value" data-implemented="false">
                Not implemented
              </span>
            </p>
            <p className="panel-note">{components.executionSafety.detail}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export interface RiskPanelProps {
  signals: RiskSignalView[];
  onRescore: () => void;
  busy: boolean;
}

export function RiskPanel({ signals, onRescore, busy }: RiskPanelProps) {
  const latest = signals[0] ?? null;
  const assessment = latest ? readAssessment(latest.rule_results) : null;

  if (latest === null) {
    return (
      <section className="panel">
        <div className="panel-head">
          <h2 className="panel-title">Risk</h2>
          <button className="btn btn-mini" type="button" onClick={onRescore} disabled={busy}>
            {busy ? 'Scoring…' : 'Score now'}
          </button>
        </div>
        <div className="panel-body">
          <div className="empty">
            <div className="empty-mark" aria-hidden="true" />
            <p className="empty-title">Not scored yet</p>
            <p className="empty-body">
              No score exists for this pair. The worker scores new pairs
              automatically; you can also score it now.
            </p>
          </div>
        </div>
      </section>
    );
  }

  const rules = assessment?.rules ?? [];
  const notable = rules.filter((rule) => rule.penalty > 0 || rule.veto);
  const passed = rules.filter((rule) => rule.penalty === 0 && !rule.veto);
  const skipped = rules.filter((rule) => rule.outcome === 'n/a');

  return (
    <section className="panel">
      <div className="panel-head">
        <h2 className="panel-title">Risk</h2>
        <span className="label label-dim">
          {relativeAge(latest.created_at)} ago · {latest.trigger}
        </span>
      </div>

      <div className="panel-body stack">
        {/* INVARIANT 3 — a veto is its own statement, stated first and plainly. */}
        {latest.has_veto && (
          <div className="hazard-notice" data-tone="alarm">
            <div className="hazard-notice-title">Vetoed — untradeable</div>
            A hard veto fired. This pair is treated as untradeable regardless of how
            its other rules came out: no combination of good liquidity or age can
            override it.
          </div>
        )}

        {/* INVARIANT 2 — before anything else, because it changes how every
            number below it should be read. */}
        {!latest.complete && (
          <div className="hazard-notice">
            <div className="hazard-notice-title">
              Partial assessment
              {skipped.length > 0 ? ` — ${skipped.length} rules skipped` : ''}
            </div>
            One or more checks did not run because their thresholds are
            unconfigured. This score reflects only the rules that could be applied
            — it is <strong>not</strong> a clean bill of health. Set the missing
            thresholds for a full assessment.
          </div>
        )}

        {assessment === null ? (
          <p className="notice">The stored rule breakdown for this signal could not be read.</p>
        ) : (
          <>
            <WhyInShort rules={rules} score={latest.score} />

            {assessment.components && (
              <ComponentBreakdown components={assessment.components} bands={assessment.verdictBands} />
            )}

            <div className="stack-sm">
              <span className="label">
                {notable.length > 0 ? 'What reduced the score' : 'Full rule breakdown'}
              </span>
              {notable.length > 0 ? (
                <ul className="rules">
                  {notable.map((rule) => (
                    <RuleRow key={rule.id} rule={rule} />
                  ))}
                </ul>
              ) : (
                <p className="notice">Every rule passed. Nothing reduced the score.</p>
              )}
            </div>

            {passed.length > 0 && (
              <details className="details-block">
                <summary>
                  {passed.length} rule{passed.length === 1 ? '' : 's'} with no penalty
                </summary>
                <ul className="rules">
                  {passed.map((rule) => (
                    <RuleRow key={rule.id} rule={rule} />
                  ))}
                </ul>
              </details>
            )}

            <dl className="facts">
              <div className="fact">
                <dt className="fact-key">Minimum liquidity</dt>
                <dd className="fact-value">
                  {assessment.thresholds.minLiquidityEth ?? 'not configured'}
                </dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Minimum pair age</dt>
                <dd className="fact-value">
                  {assessment.thresholds.minPairAgeSeconds === null
                    ? 'not configured'
                    : `${assessment.thresholds.minPairAgeSeconds}s`}
                </dd>
              </div>
              <div className="fact">
                <dt className="fact-key">Ruleset</dt>
                <dd className="fact-value">
                  {latest.ruleset_version}
                  {latest.stale_ruleset ? ` (current ${RULESET_VERSION})` : ''}
                </dd>
              </div>
            </dl>
          </>
        )}

        {/* INVARIANT 4 — below the rules, secondary, model-labelled. Never above. */}
        {latest.ai_summary !== null && (
          <div className="ai-summary">
            <span className="label label-dim">
              AI explanation{latest.ai_model ? ` · ${latest.ai_model}` : ''}
            </span>
            <p className="ai-summary-text">{latest.ai_summary}</p>
            <p className="ai-summary-note">
              Written by a language model to explain the rules above. It did not
              produce the score and cannot change it.
            </p>
          </div>
        )}

        {signals.length > 1 && (
          <details className="details-block">
            <summary>Score history ({signals.length - 1} earlier)</summary>
            <ul className="rules">
              {signals.slice(1).map((signal) => (
                <li className="rule" key={signal.id} data-outcome="pass">
                  <span className="rule-bar" aria-hidden="true" />
                  <span>
                    <span className="rule-label">
                      {signal.score} · {signal.verdict}
                    </span>
                    <span className="rule-values" title={absoluteTime(signal.created_at)}>
                      {relativeAge(signal.created_at)} ago · {signal.trigger}
                    </span>
                  </span>
                </li>
              ))}
            </ul>
          </details>
        )}
      </div>
    </section>
  );
}

/** Rescore control, lifted out so the detail header can own it. */
export function RescoreButton({ onRescore, busy }: { onRescore: () => void; busy: boolean }) {
  return (
    <button className="btn" type="button" onClick={onRescore} disabled={busy}>
      {busy ? 'Rescoring…' : 'Rescore'}
    </button>
  );
}
